/* eslint-disable no-nested-ternary */

import OpenInNew from '@mui/icons-material/OpenInNew';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import { withStyles } from '@mui/styles';
import PropTypes from 'prop-types';
import {
  useDispatch,
  useSelector,
} from 'react-redux';

import {
  AttachMoney,
  ExpandCircleDown,
  MoneyOff,
  Repeat,
  Task,
} from '@mui/icons-material';
import {
  Badge,
  Checkbox,
  Chip,
  Tooltip,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import { groupBy } from 'lodash';
import moment from 'moment';
import Horse from '../../../../components/Horse';
import User from '../../../../components/User';
import billingImage from '../../../../images/undraw_printing_invoices_-5-r4r.svg';
import { setGlobalModals } from '../../../../redux/app/actions';
import { TASK_MODAL_TYPE } from '../../../../redux/app/types';
import { primary } from '../../../../utils/brandColors';
import styles from '../Billables/styles';
import LoadingBillableRow from '../LoadingBillableRow';
import QuickBooksReservation from '../../../../components/QuickBooksReservation';
import { isQuickBooksReserved } from '../../../../utils/quickBooksReservation';

export const getBillableValue = (value) => {
  if (!value || Number.isNaN(value)) {
    return '0.00';
  }
  return parseFloat(value).toFixed(2)
}

export const getColorForBillableStatus = (billable) => {
  if (billable.status === 'Expense') { return undefined }
  if (billable.status === 'Void') {
    return 'secondary'
  } if (billable.status === 'Paid') {
    return 'success'
  }
  return 'primary'
}

const getPaymentStatusChip = (billable, onDelete, replacementText) => {
  if (!billable.receivable || billable.status === 'Expense') {
    return (
      <Tooltip title="Expenses have no status and always show as paid out in reporting.">
        <Chip label={replacementText || 'Expense'} onDelete={onDelete && ((e) => { e.stopPropagation(); onDelete(e); })} variant="outlined" deleteIcon={<ExpandCircleDown />} />
      </Tooltip>
    )
  }
  if (billable.status === 'Void') {
    return <Chip label={replacementText || 'Void'} color={getColorForBillableStatus(billable)} variant="outlined" onDelete={onDelete && ((e) => { e.stopPropagation(); onDelete(e); })} deleteIcon={<ExpandCircleDown />} />
  } if (billable.status === 'Paid') {
    return <Chip label={replacementText || 'Paid'} color={getColorForBillableStatus(billable)} variant="outlined" onDelete={onDelete && ((e) => { e.stopPropagation(); onDelete(e); })} deleteIcon={<ExpandCircleDown />} />
  }
  return <Chip label={replacementText || 'Open'} color={getColorForBillableStatus(billable)} variant="outlined" onDelete={onDelete && ((e) => { e.stopPropagation(); onDelete(e); })} deleteIcon={<ExpandCircleDown />} />
}

export const getChipForStatus = (billable, onDelete, replacementText) => (
  <Box
    component="span"
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      flexWrap: 'wrap',
      gap: 0.5,
    }}
  >
    {getPaymentStatusChip(billable, isQuickBooksReserved(billable)
      ? undefined
      : onDelete, replacementText)}
    <QuickBooksReservation billable={billable} />
  </Box>
);

const BillablesTableComponent = ({
  classes,
  fullscreen,
  isFetchingBillables,
  billables,
  page,
  pageSize,
  setPageSize,
  setPage,
  setSelectedBillable,
  setOpenAddEditBillableModal,
  setOpenStatusChangeMenu,
  setStatusChangeMenuAnchorEl,
  selectMode,
  selectedHorseBillables,
  onChangeSelectedHorseBillables,
  reducedColumns,
}) => {
  const dispatch = useDispatch();

  const eventTemplates = useSelector((state) => state.eventTemplate.eventTemplates);
  const eventTemplatesMap = groupBy(eventTemplates, 'id');

  const handleAlternativeTableRowSelect = (horseBillable) => {
    if (selectMode && onChangeSelectedHorseBillables) {
      if (selectedHorseBillables.find((hb) => hb.id === horseBillable.id)) {
        // remove
        onChangeSelectedHorseBillables([...selectedHorseBillables.filter((hb) => hb.id !== horseBillable.id)])
      } else if (!isQuickBooksReserved(horseBillable)) {
        // add
        onChangeSelectedHorseBillables([...selectedHorseBillables, horseBillable])
      }
    }
  }

  let finalBillables = billables.content;

  if (selectMode) {
    // Put selected at top then filter out selected from main list
    finalBillables = [...selectedHorseBillables.map((hb) => ({
      ...hb,
      selected: true,
    })),
    ...finalBillables.filter((hb) => !selectedHorseBillables.find((shb) => shb.id === hb.id))]
  }

  return (!isFetchingBillables && billables.totalElements === 0
    ? (
      <Box
        sx={{
          margin: 'auto',
          p: 2,
          textAlign: 'center',
        }}
      >
        <Typography variant="h5" textAlign="center" gutterBottom>
          No Transactions Found!
        </Typography>
        <Typography
          variant="captions"
          align="center"
          component="div"
          sx={{
            fontWeight: 300,
            mb: 2,
          }}
        >
          You can create transactions manually or have them automatically created when a task is completed.
        </Typography>
        <img
          src={billingImage}
          alt="Team printing invoice"
          style={{
            width: 400,
            display: 'block',
            margin: 'auto',
            marginBottom: '1rem',
          }}
        />
      </Box>
    )
    : (
      <Paper classes={{ root: classes.paperRoot }}>
        <Table size="small" className={classes.table}>
          <TableHead>
            <TableRow>
              {
                selectMode && <TableCell />
              }
              <TableCell>Description</TableCell>
              {!fullscreen && <TableCell>Date</TableCell>}
              <TableCell>{!fullscreen
                ? 'Horse'
                : 'Info'}
              </TableCell>
              {!fullscreen && <TableCell>User(s)</TableCell>}
              {!fullscreen && !reducedColumns && <TableCell>Transaction ID</TableCell>}
              <TableCell
                align={fullscreen
                  ? 'right'
                  : undefined}
              >{!fullscreen
                ? 'Status'
                : 'Details'}
              </TableCell>
              {!fullscreen && <TableCell align="right">Amount</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {
              isFetchingBillables
                ? [1, 2, 3, 4, 5].map((item) => (
                  <LoadingBillableRow key={`loading-item-${item}`} classes={classes} />
                ))
                : (finalBillables.map((billable) => {
                  const color = (eventTemplatesMap[billable.taskTypeId]
                    && eventTemplatesMap[billable.taskTypeId][0].eventDataDefaultValues
                    && eventTemplatesMap[billable.taskTypeId][0].eventDataDefaultValues.calendarDisplayColor) || primary[500];

                  return (
                    <TableRow
                      className={billable.void
                        ? classes.rowInactive
                        : classes.row}
                      hover
                      key={`billable-item-${billable.id}-${billable.selected
                        ? 'selected'
                        : ''}`}
                      onClick={setSelectedBillable
                        ? (() => { setOpenAddEditBillableModal(true); setSelectedBillable(billable); })
                        : (() => handleAlternativeTableRowSelect(billable))}
                    >
                      {
                        selectMode
                        && (
                          <TableCell padding="checkbox">
                            <Checkbox
                              color="primary"
                              checked={!!billable.selected}
                              disabled={isQuickBooksReserved(billable) && !billable.selected}
                              inputProps={{ 'aria-label': `Select ${billable.note || 'transaction'}` }}
                            />
                          </TableCell>
                        )
                      }
                      <TableCell
                        sx={[{ maxWidth: 200 }, fullscreen
                          ? { verticalAlign: 'top' }
                          : { verticalAlign: 'inherit' }]}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                          }}
                        >
                          <Badge
                            anchorOrigin={{
                              vertical: 'bottom',
                              horizontal: 'right',
                            }}
                            badgeContent={billable.repeatNextOccurrenceDate && (
                              <Repeat
                                color="secondary"
                                sx={{
                                  fontSize: 14,
                                  fontWeight: 700,
                                }}
                              />
                            )}
                          >
                            {eventTemplatesMap[billable.repeatTaskTypeId || billable.taskTypeId]
                              ? <Task sx={{ color }} />
                              : (billable.receivable
                                ? <AttachMoney sx={{ color }} />
                                : <MoneyOff />)}
                          </Badge>
                          <Typography
                            component="div"
                            sx={{
                              display: '-webkit-box',
                              overflow: 'hidden',
                              WebkitBoxOrient: 'vertical',
                              WebkitLineClamp: 3,
                              fontWeight: 700,
                            }}
                          >{billable.note}{billable.quantity && ` (qty ${billable.quantity})`}
                          </Typography>
                        </Box>
                        {billable.taskId
                          && (
                            <Button
                              endIcon={<OpenInNew />}
                              sx={{
                                p: 0,
                                mt: 0.5,
                              }}
                              size="small"
                              onClick={(e) => {
                                dispatch(setGlobalModals(TASK_MODAL_TYPE, { id: billable.taskId }, null, null, {}, null));
                                e.stopPropagation();
                              }}
                            >Task (#{billable.taskId})
                            </Button>
                          )}
                        {
                          fullscreen && (
                            (!billable.barnUsers || billable.barnUsers.length === 0)
                              ? '—'
                              : (
                                <Grid container sx={{ mt: 1 }}>
                                  {billable.barnUsers.map((bu) => <Grid key={`billable-bu-${billable.bu}-${bu.id}`}><User chip user={bu} /></Grid>)}
                                </Grid>
                              )
                          )
                        }
                      </TableCell>
                      {
                        !fullscreen && (
                          <TableCell sx={{ fontWeight: 700 }}>
                            <Tooltip
                              title={billable.receivable
                                ? 'Billable'
                                : 'Expense'}
                            >
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                }}
                              >
                                <Box>{moment(billable.billableDate).format('ll')}</Box>
                              </Box>
                            </Tooltip>
                          </TableCell>
                        )
                      }
                      <TableCell>
                        {fullscreen && (
                          <Box
                            sx={{
                              fontWeight: 700,
                              mb: 1,
                            }}
                          >{moment(billable.billableDate).format('ll')}
                          </Box>
                        )}
                        <Horse
                          size={!fullscreen
                            ? 50
                            : 25}
                          flexDirection={fullscreen
                            ? 'column'
                            : 'row'}
                          align={fullscreen
                            ? 'center'
                            : 'flex-start'}
                          horse={billable.horse}
                          textWidth={85}
                        />
                      </TableCell>
                      {
                        !fullscreen && (
                          <TableCell sx={{ maxWidth: 150 }}>
                            {
                              (!billable.barnUsers || billable.barnUsers.length === 0)
                                ? '—'
                                : (
                                  <Grid container spacing={1}>
                                    {billable.barnUsers.map((bu) => <Grid key={`billable-bu-${billable.id}-${bu.id}`}><User chip user={bu} /></Grid>)}
                                  </Grid>
                                )
                            }
                          </TableCell>
                        )
                      }
                      {
                        !fullscreen && !reducedColumns && (
                          <TableCell>
                            {billable.id}
                          </TableCell>
                        )
                      }
                      <TableCell>
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'flex-end',
                          }}
                        >
                          {
                            fullscreen && (
                              <Typography
                                align="right"
                                sx={{
                                  fontWeight: 600,
                                  mb: 1,
                                  color: billable.receivable
                                    ? '#2e7d32'
                                    : 'inherit',
                                }}
                              >${getBillableValue(billable.price)}
                              </Typography>
                            )
                          }
                          <Box
                            sx={fullscreen
                              ? { textAlign: 'right' }
                              : {}}
                          >
                            {getChipForStatus(billable, setSelectedBillable && ((e) => {
                              setSelectedBillable(billable);
                              setOpenStatusChangeMenu(true);
                              setStatusChangeMenuAnchorEl(e.currentTarget);
                            }))}
                          </Box>
                        </Box>
                      </TableCell>
                      {
                        !fullscreen && (
                          <TableCell>
                            <Typography
                              align="right"
                              sx={{
                                fontWeight: 600,
                                color: billable.receivable
                                  ? '#2e7d32'
                                  : 'inherit',
                              }}

                            >${getBillableValue(billable.price)}
                            </Typography>
                            {billable.paymentNote && (
                              <Box sx={{ maxWidth: 150 }}>
                                <Typography
                                  align="right"
                                  variant="caption"
                                  sx={{
                                    fontWeight: 300,
                                    width: '100%',
                                    display: '-webkit-box',
                                    overflow: 'hidden',
                                    WebkitBoxOrient: 'vertical',
                                    WebkitLineClamp: 3,
                                  }}
                                >
                                  {billable.paymentNote}
                                </Typography>
                              </Box>
                            )}
                          </TableCell>
                        )
                      }
                    </TableRow>
                  );
                }))
            }
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={billables.totalElements || 0}
          rowsPerPage={pageSize}
          page={page}
          onPageChange={(event, newPage) => setPage(newPage)}
          onRowsPerPageChange={(event) => { setPage(0); setPageSize(parseInt(event.target.value, 10)); }}
        />
      </Paper>
    ));
};

BillablesTableComponent.propTypes = {
  billables: PropTypes.any,
  fullscreen: PropTypes.bool.isRequired,
  classes: PropTypes.object,
  isFetchingBillables: PropTypes.bool.isRequired,
  page: PropTypes.number.isRequired,
  pageSize: PropTypes.number.isRequired,
  setPage: PropTypes.func.isRequired,
  setPageSize: PropTypes.func.isRequired,
  setSelectedBillable: PropTypes.func,
  setOpenAddEditBillableModal: PropTypes.func,
  setOpenStatusChangeMenu: PropTypes.func,
  setStatusChangeMenuAnchorEl: PropTypes.func,
  selectMode: PropTypes.bool,
  selectedHorseBillables: PropTypes.array,
  onChangeSelectedHorseBillables: PropTypes.func,
  reducedColumns: PropTypes.bool,
};

BillablesTableComponent.defaultProps = {
  classes: {},
  billables: { content: [] },
  setSelectedBillable: () => { },
  setOpenAddEditBillableModal: () => { },
  setOpenStatusChangeMenu: null,
  setStatusChangeMenuAnchorEl: null,
  selectMode: false,
  selectedHorseBillables: [],
  onChangeSelectedHorseBillables: () => { },
  reducedColumns: false,
};

export default withStyles(styles)(BillablesTableComponent);
